import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import cookie from "react-cookies";
import {
  extractCertificate,
  getAcademyCertificates,
  getCertificationEligibility,
} from "../../services/academyApi";
import { QtBackLink } from "../../components/icons/QtIcon";
import {
  TrainingHero,
  TrainingLoading,
  TrainingFeedback,
  CertAvailabilityNote,
  TrainingCertLadder,
  groupIssuedCertificatesByProgram,
  certDetailPath,
  CertDownloadButton,
} from "../academy/AcademyShared";
import MeLayout from "./MeLayout";

function CertStatusBadge({ status }) {
  const labels = {
    issued: "Issued",
    eligible: "Ready to claim",
    in_progress: "In progress",
    locked: "Locked",
  };
  return (
    <span className={`training-cert-badge training-cert-badge--${status || "locked"}`}>
      {labels[status] || status}
    </span>
  );
}

export default function MeCertificates() {
  const [issued, setIssued] = useState([]);
  const [ladder, setLadder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [certRes, ladderRes] = await Promise.all([
        getAcademyCertificates(),
        getCertificationEligibility(),
      ]);
      setIssued(certRes.certificates || []);
      setLadder(ladderRes.certifications || []);
    } catch {
      setFeedback({ type: "error", message: "Could not load certificates." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = "/login?next=/me/certificates";
      return;
    }
    load();
  }, [load]);

  const handleDownload = async (certId) => {
    setExtracting(certId);
    setFeedback(null);
    try {
      const res = await extractCertificate(certId);
      if (res.download_url) {
        window.open(res.download_url, "_blank", "noopener,noreferrer");
        setFeedback({ type: "success", message: "Certificate download started." });
        await load();
      }
    } catch (e) {
      setFeedback({ type: "error", message: e.message || "Download failed." });
    } finally {
      setExtracting(null);
    }
  };

  const copyVerifyLink = (number) => {
    const url = `${window.location.origin}/verify/${number}`;
    navigator.clipboard?.writeText(url);
    setFeedback({ type: "success", message: "Verify link copied." });
  };

  if (loading) {
    return (
      <MeLayout title="Certificates">
        <TrainingLoading label="Loading certificates..." />
      </MeLayout>
    );
  }

  const activeIssued = issued.filter((c) => c.status === "issued");
  const issuedByProgram = groupIssuedCertificatesByProgram(activeIssued);
  const programCount = issuedByProgram.length;

  return (
    <MeLayout title="Certificates">
      <TrainingHero
        eyebrow="Your credentials"
        title="Academy certificates"
        subtitle={
          activeIssued.length > 0
            ? `${activeIssued.length} certificate${activeIssued.length === 1 ? "" : "s"} earned across ${programCount} ${programCount === 1 ? "program" : "programs"}. Download PDFs or track the next tier below.`
            : "Download PDFs you have earned and track the next tier on your ladder."
        }
      />

      <CertAvailabilityNote />

      <TrainingFeedback feedback={feedback} />

      {activeIssued.length > 0 && (
        <>
          <h3 className="training-section-title">Your certificates</h3>
          {issuedByProgram.map((group) => (
            <div key={group.programSlug} className="training-cert-issued-group">
              <h4 className="training-cert-issued-group__title">
                {group.programTitle}
                <span className="training-cert-issued-group__count">
                  {group.certificates.length} earned
                </span>
              </h4>
              {group.certificates.map((c) => (
                <div key={c.id} className="training-cert-card training-cert-card--issued">
                  <div className="training-cert-card__head">
                    <div>
                      <Link
                        to={certDetailPath(group.programSlug, c.certification_slug || c.slug)}
                        className="training-cert-card__title-link"
                      >
                        <strong>{c.title}</strong>
                      </Link>
                      <p className="training-cert-card__meta">{c.certificate_number}</p>
                      <p className="training-cert-card__meta">
                        Issued {new Date(c.issued_at).toLocaleDateString("en-ZA")}
                      </p>
                    </div>
                    <CertStatusBadge status="issued" />
                  </div>
                  <div className="training-cert-card__actions">
                    <CertDownloadButton
                      loading={extracting === c.id}
                      onClick={() => handleDownload(c.id)}
                    />
                    <Link
                      to={certDetailPath(group.programSlug, c.certification_slug || c.slug)}
                      className="training-btn-secondary"
                      style={{ textDecoration: "none" }}
                    >
                      Certificate details
                    </Link>
                    <button
                      type="button"
                      className="training-btn-secondary"
                      onClick={() => copyVerifyLink(c.certificate_number)}
                    >
                      Copy verify link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      <h3 className="training-section-title">Certification ladder</h3>
      <TrainingCertLadder
        certifications={ladder}
        showProgramHeaders
        showBlockers
        onDownload={handleDownload}
        extractingId={extracting}
      />

      <QtBackLink to="/academy" className="training-btn-secondary" style={{ marginTop: 20 }}>
        Back to programs
      </QtBackLink>
    </MeLayout>
  );
}
